import { useEffect, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENABLED } from './supabase';
import { logger } from './logger';
import {
  loadSessions, loadGoals, updateSettings,
  addSession, updateSession, deleteSession as reduxDeleteSession,
  addGoal, updateGoal, deleteGoal as reduxDeleteGoal,
  Session, Goal, AppSettings,
} from '../store';
import {
  fetchSessions, upsertSession, upsertSessions,
  deleteSession as dbDeleteSession, deleteAllSessions,
  fetchGoals, upsertGoal, deleteGoal as dbDeleteGoal,
  fetchProfile, updateProfile,
} from './db';

export function useDB(user: User | null) {
  const dispatch = useDispatch();
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    if (!user || !SUPABASE_ENABLED) { setSynced(false); return; }

    setSyncing(true);
    Promise.all([
      fetchSessions(user.id),
      fetchGoals(user.id),
      fetchProfile(user.id),
    ]).then(([sessions, goals, profile]) => {
      dispatch(loadSessions(sessions));
      dispatch(loadGoals(goals));
      dispatch(updateSettings({ initialBankroll: profile.initialBankroll, currency: profile.currency }));
      setSynced(true);
    }).catch(err => logger.error('useDB', 'Error al cargar datos iniciales del usuario', err))
      .finally(() => setSyncing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const saveSession = useCallback(async (session: Session) => {
    dispatch(addSession(session));
    if (user && SUPABASE_ENABLED)
      upsertSession(session, user.id).catch(err => logger.error('useDB', 'Error al guardar sesión', err, { sessionId: session.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const editSession = useCallback(async (session: Session) => {
    dispatch(updateSession(session));
    if (user && SUPABASE_ENABLED)
      upsertSession(session, user.id).catch(err => logger.error('useDB', 'Error al editar sesión', err, { sessionId: session.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const removeSession = useCallback(async (id: string) => {
    dispatch(reduxDeleteSession(id));
    if (user && SUPABASE_ENABLED)
      dbDeleteSession(id).catch(err => logger.error('useDB', 'Error al eliminar sesión', err, { sessionId: id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const resetAll = useCallback(async () => {
    dispatch(loadSessions([]));
    if (user && SUPABASE_ENABLED)
      deleteAllSessions(user.id).catch(err => logger.error('useDB', 'Error al resetear todas las sesiones', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const saveGoal = useCallback(async (goal: Goal) => {
    dispatch(addGoal(goal));
    if (user && SUPABASE_ENABLED)
      upsertGoal(goal, user.id).catch(err => logger.error('useDB', 'Error al guardar goal', err, { goalId: goal.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const editGoal = useCallback(async (goal: Goal) => {
    dispatch(updateGoal(goal));
    if (user && SUPABASE_ENABLED)
      upsertGoal(goal, user.id).catch(err => logger.error('useDB', 'Error al editar goal', err, { goalId: goal.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const removeGoal = useCallback(async (id: string) => {
    dispatch(reduxDeleteGoal(id));
    if (user && SUPABASE_ENABLED)
      dbDeleteGoal(id).catch(err => logger.error('useDB', 'Error al eliminar goal', err, { goalId: id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const saveSettings = useCallback(async (settings: Partial<AppSettings>) => {
    dispatch(updateSettings(settings));
    if (user && SUPABASE_ENABLED)
      updateProfile(user.id, settings).catch(err => logger.error('useDB', 'Error al guardar settings', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const importSessions = useCallback(async (sessions: Session[]) => {
    if (!user) return;

    // 1. Actualizar Redux de inmediato para feedback visual
    sessions.forEach(s => dispatch(addSession(s)));

    if (SUPABASE_ENABLED) {
      try {

        await upsertSessions(sessions, user.id);


        const populationStats = sessions.flatMap(session =>
          (session.tournaments || []).map(t => {
            // Usamos 'as any' o 'ParsedTournament' para acceder a los counts que 
            // ahora sí estamos pasando desde ImportView
            const pt = t as any;

            return {
              user_id: user.id,
              tournament_id: pt.id,
              date: session.date,
              format: pt.format,
              buy_in: pt.buyIn,
              stake_range: getStakeRange(pt.buyIn), // Función auxiliar para tu migration

              // Mapeo de columnas SQL desde los contadores de PokerCraft
              hands_played: pt.handsPlayed,
              vpip: Math.round((pt.vpipCount / pt.handsPlayed) * 100) || 0,
              pfr: Math.round((pt.pfrCount / pt.handsPlayed) * 100) || 0,
              threebet: pt.threebetOpp > 0 ? Math.round((pt.threebetCount / pt.threebetOpp) * 100) : 0,
              fold_to_3bet: pt.foldTo3betOpp > 0 ? Math.round((pt.foldTo3betCount / pt.foldTo3betOpp) * 100) : 0,
              ats: pt.atsOpp > 0 ? Math.round((pt.atsCount / pt.atsOpp) * 100) : 0,
              af: pt.af || 0,
              afq: pt.afq || 0,
              // ... agregar aquí el resto de columnas de tu migration_001
              wtsd: pt.flopsSeen > 0 ? Math.round((pt.wtsdCount / pt.flopsSeen) * 100) : 0,
              wwsf: pt.wwsfOpp > 0 ? Math.round((pt.wwsfCount / pt.wwsfOpp) * 100) : 0,
              wsd: pt.wsdOpp > 0 ? Math.round((pt.wsdCount / pt.wsdOpp) * 100) : 0
            };
          })
        );

        if (populationStats.length > 0) {
          const { error } = await supabase
            .from('player_stats')
            .insert(populationStats);

          if (error) logger.error('useDB', 'Error al guardar population stats', error, { count: populationStats.length });
        }

      } catch (err) {
        logger.error('useDB', 'Error en la sincronización de sesiones', err, { sessionCount: sessions.length });
      }
    }
  }, [user, dispatch]);

  // Función auxiliar simple para categorizar stakes
  function getStakeRange(buyIn: number): string {
    if (buyIn < 5) return '$1-5';
    if (buyIn < 15) return '$5-15';
    if (buyIn < 50) return '$15-50';
    return '$50+';
  }

  return {
    syncing, synced,
    saveSession, editSession, removeSession, resetAll,
    saveGoal, editGoal, removeGoal,
    saveSettings, importSessions,
  };
}

export type DBActions = ReturnType<typeof useDB>;
